// // MODIFIED: Added submission guards
import { useState, useCallback, useEffect } from "react";
import changeScheduleService from "../services/changeScheduleService";
import { useGeneralMessage } from "../../common/APICommon";
import { compressImage } from "../../utils/fileUtils";

export const useChangeSchedule = (user) => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [shiftSchedules, setShiftSchedules] = useState([]);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const [formData, setFormData] = useState({
    dateFrom: "",
    dateTo: "",
    shiftScheduleFromId: "",
    shiftScheduleToId: "",
    reason: "",
    content: null,
    fileName: "",
    fileType: ""
  });

  const fetchData = useCallback(async () => {
    if (!user?.employeePersonalInformationId) return;
    try {
      setLoading(true);
      const [histRes, shiftRes] = await Promise.all([
        changeScheduleService.getByEmployee(user.employeePersonalInformationId),
        changeScheduleService.getShiftSchedules()
      ]);
      setRequests(histRes.data);
      setShiftSchedules(shiftRes.data.map(s => ({
        value: s.id,
        label: `${s.id} - ${s.shiftName}`
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (file) => {
    if (!file) return;
    const MAX_SIZE_MB = 5;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      showGeneralMessage(`File is too large. Max ${MAX_SIZE_MB}MB.`, false);
      return;
    }
    try {
      const reader = new FileReader();
      if (file.type.startsWith("image/")) {
        const compressed = await compressImage(file);
        reader.readAsDataURL(compressed);
      } else {
        reader.readAsDataURL(file);
      }
      reader.onload = () => {
        const base64String = reader.result;
        const photoToBeSaved = base64String.split(",")[1];
        setFormData(prev => ({ ...prev, content: photoToBeSaved, fileName: file.name, fileType: file.type }));
      };
    } catch (err) {
      showGeneralMessage("Failed to process file.", false);
    }
  };

  // // MODIFIED: Logic now strictly gated by loading state
  const submitRequest = async () => {
    if (loading) return false; // // ADDED: Guard clause

    setLoading(true);
    try {
      const payload = {
        ...formData,
        employeePersonalInformationId: user.employeePersonalInformationId,
        requestTypeId: 2,
        status: "Sent",
        dateRequested: new Date().toISOString(),
        dateCompleted: new Date().toISOString(),
      };

      await changeScheduleService.create(payload);

      showGeneralMessage("Change Schedule request submitted successfully!", true);
      fetchData();

      // Reset Form
      setFormData({
        dateFrom: "", dateTo: "", shiftScheduleFromId: "",
        shiftScheduleToId: "", reason: "", content: null, fileName: "", fileType: ""
      });
      return true;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to submit request";
      showGeneralMessage(msg, false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, requests, shiftSchedules, formData,
    handleInputChange, handleFileChange, submitRequest,
    showMessage, message, isSuccess
  };
};